import { Link, NavLink as RouterLink, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useIntl } from 'react-intl';
import { isNumber, substringBefore } from 'utils/stringUtils';
import { useMemo } from 'react';
import { containsVariable, NOT_FOUND } from 'utils/messageUtils';
import { Helmet } from 'react-helmet-async';
import MDLink from 'components/MDLink';
import { useDispatch, useSelector } from 'react-redux';
import { updateBackLinkDisplayed } from 'slice/appSlice';

function Breadcrumbs({ routePath, view, homeIcon, backIcon, light, pageTitle }) {
  const { formatMessage: msg } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appState = useSelector((state) => state.app);

  // Removes the first element (portal) and the "view" element
  let effectiveRoutePath = routePath.slice(1);
  if (effectiveRoutePath.length <= 1) {
    // static view
  } else {
    // dynamic view: first token defines the view type / second token defines the view name / other tokes defines view details
    effectiveRoutePath = effectiveRoutePath.filter((item, index) => {
      if ((index === 1 && item === view) || item === '') {
        return false;
      }
      return !isNumber(substringBefore(item, ';')) && !isMD5(item);
    });
  }
  const currentPageName = effectiveRoutePath.slice(-1);

  const title = useMemo(() => {
    let title = pageTitle;
    if (!title) {
      title = msg({
        id: `view.${view}.title`,
        defaultMessage: NOT_FOUND
      });
      if (title === NOT_FOUND || containsVariable(title)) {
        title = msg({ id: `page.${currentPageName}` });
      }
    }
    return title;
  }, [currentPageName, msg, pageTitle, view]);

  const handleBackClick = () => {
    dispatch(updateBackLinkDisplayed(false));
    navigate(-1);
  };

  return (
    <MDBox mr={1}>
      <MuiBreadcrumbs
        aria-label={msg({ id: `common.breadcrumbs.label` })}
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600])
          }
        }}
      >
        {backIcon && appState.backLinkDiplayed && (
          <MDLink
            aria-label={msg({ id: `common.back` })}
            onClick={handleBackClick}
            sx={{ cursor: 'pointer' }}
          >
            <MDTypography
              component="span"
              variant="body2"
              color={light ? 'white' : 'dark'}
              sx={{ lineHeight: 0, opacity: 0.8 }}
            >
              <Icon>{backIcon}</Icon>
            </MDTypography>
          </MDLink>
        )}
        {homeIcon && (
          <Link to="/home" aria-label={msg({ id: `page.home.label` })}>
            <MDTypography
              component="span"
              variant="body2"
              color={light ? 'white' : 'dark'}
              sx={{ lineHeight: 0, opacity: 0.8 }}
            >
              <Icon>{homeIcon}</Icon>
            </MDTypography>
          </Link>
        )}
        {effectiveRoutePath.slice(0, -1).map((el, index) => (
          <Link
            component={RouterLink}
            aria-label={
              index === 0
                ? msg({ id: `view.${view}.shortTitle`, defaultMessage: msg({ id: `page.${el}` }) })
                : msg({ id: `page.${el}` })
            }
            to={`${el}/${view || ''}`}
            key={el}
            state={{
              view
            }}
          >
            <MDTypography
              component="span"
              variant="button"
              color={light ? 'white' : 'dark'}
              sx={{
                height: 1,
                fontWeight: 'regular',
                textTransform: 'capitalize',
                opacity: 0.8,
                textDecoration: 'underline'
              }}
            >
              {index === 0
                ? msg({ id: `view.${view}.shortTitle`, defaultMessage: msg({ id: `page.${el}` }) })
                : msg({ id: `page.${el}` })}
            </MDTypography>
          </Link>
        ))}
        <MDTypography
          variant="button"
          color={light ? 'white' : 'dark'}
          sx={{ height: 1, fontWeight: 'regular', textTransform: 'capitalize' }}
        >
          {effectiveRoutePath.length === 1
            ? msg({
                id: `view.${view}.shortTitle`,
                defaultMessage: msg({ id: `page.${currentPageName}` })
              })
            : msg({
                id: `view.${view}.${effectiveRoutePath.slice(1).join('.')}.shortTitle`,
                defaultMessage: msg({ id: `page.${currentPageName}` })
              })}
        </MDTypography>
      </MuiBreadcrumbs>
      <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h1"
        color={light ? 'white' : 'dark'}
        noWrap
      >
        {title}
      </MDTypography>
      <Helmet>
        <title>
          {title} - {msg({ id: 'product.name' })}
        </title>
      </Helmet>
    </MDBox>
  );
}

function isMD5(hash) {
  const md5Regex = /^[0-9a-fA-F]{32}$/;
  return md5Regex.test(hash);
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  routePath: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  view: PropTypes.string,
  homeIcon: PropTypes.node,
  backIcon: PropTypes.node,
  light: PropTypes.bool,
  pageTitle: PropTypes.string
};

export default Breadcrumbs;
